import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { pipes, type PipeCard, type PipeSummary } from '@/shared/api/resources';
import { formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { SkeletonText } from '@/shared/ui/Skeleton';

import {
  caminhoDoCartao,
  resumoDoCartao,
  TAMANHO_MINIMO_DA_BUSCA,
} from './resultadoDaBusca';

/** Cartoes por esteira na busca: a lista e de achados, nao o quadro inteiro. */
const LIMITE_POR_ESTEIRA = 10;
const ESPERA_DA_DIGITACAO_MS = 350;

/**
 * Busca em todas as esteiras (pedido do dono de 03/11/2026).
 *
 * Para achar um cartao era preciso abrir esteira por esteira. A busca usa a busca livre
 * de cada quadro, a mesma do campo Buscar dele (numero da operacao ou da cotacao, objeto,
 * tomador pelo nome ou CNPJ e corretora), em todas as esteiras ao mesmo tempo, e diz em
 * que esteira e coluna esta cada cartao. O clique abre o quadro com a busca aplicada e o
 * cartao aberto. Os cartoes encerrados entram, marcados, como no quadro.
 */
export function BuscaNasEsteiras({ esteiras }: { esteiras: PipeSummary[] }) {
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');

  // A busca espera a pessoa parar de digitar: uma consulta por esteira a cada tecla
  // seria uma rajada de pedidos para cada letra.
  useEffect(() => {
    const texto = digitado.trim();
    const espera = window.setTimeout(() => setBusca(texto), ESPERA_DA_DIGITACAO_MS);
    return () => window.clearTimeout(espera);
  }, [digitado]);

  const ativa = busca.length >= TAMANHO_MINIMO_DA_BUSCA;
  const consultas = useQueries({
    queries: esteiras.map((esteira) => ({
      queryKey: ['pipes', esteira.id, 'cards', { q: busca, limit: LIMITE_POR_ESTEIRA }],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        pipes.cards(esteira.id, { q: busca, limit: LIMITE_POR_ESTEIRA }, signal),
      enabled: ativa,
    })),
  });

  const carregando = ativa && consultas.some((consulta) => consulta.isPending);
  const comAchados = esteiras
    .map((esteira, indice) => ({ esteira, consulta: consultas[indice] }))
    .filter(({ consulta }) => (consulta?.data?.items.length ?? 0) > 0);
  const falhas = esteiras.filter((_, indice) => consultas[indice]?.isError);
  const total = comAchados.reduce(
    (soma, { consulta }) => soma + (consulta?.data?.items.length ?? 0),
    0,
  );

  return (
    <div className="flex flex-col gap-4">
      <Field
        label="Buscar em todas as esteiras"
        type="search"
        value={digitado}
        placeholder="Tomador, CNPJ, numero da cotacao, objeto ou corretora"
        hint={
          digitado.trim() !== '' && digitado.trim().length < TAMANHO_MINIMO_DA_BUSCA
            ? `Digite pelo menos ${TAMANHO_MINIMO_DA_BUSCA} letras ou numeros.`
            : 'Procura nas esteiras que voce ve e diz em qual esteira e coluna esta cada cartao.'
        }
        onChange={(event) => setDigitado(event.target.value)}
      />

      {ativa ? (
        <section
          aria-label="Resultado da busca nas esteiras"
          className="flex flex-col gap-3"
        >
          {falhas.length > 0 ? (
            <Alert tone="warning" title="Nem todas as esteiras responderam">
              Sem resposta de {falhas.map((esteira) => esteira.name).join(', ')}. O que
              aparece abaixo e so das outras.
            </Alert>
          ) : null}

          {carregando && comAchados.length === 0 ? (
            <Card>
              <CardBody>
                <SkeletonText lines={3} />
              </CardBody>
            </Card>
          ) : comAchados.length === 0 && !carregando ? (
            <p className="text-sm text-ink-500">
              Nenhum cartao encontrado para "{busca}" nas esteiras.
            </p>
          ) : (
            <p className="text-sm text-ink-600">
              {total === 1 ? '1 cartao encontrado' : `${total} cartoes encontrados`}{' '}
              para "{busca}".
            </p>
          )}

          {comAchados.map(({ esteira, consulta }) => {
            const achados = consulta?.data?.items ?? [];
            const totalDaEsteira = consulta?.data?.total ?? achados.length;
            return (
              <Card key={esteira.id}>
                <CardHeader
                  title={esteira.name}
                  description={
                    totalDaEsteira > achados.length
                      ? `${achados.length} de ${totalDaEsteira} cartoes. Abra a esteira para ver todos.`
                      : achados.length === 1
                        ? '1 cartao'
                        : `${achados.length} cartoes`
                  }
                  action={
                    <Link
                      to={caminhoDoCartao(esteira.id, busca)}
                      className="text-sm font-medium text-brand-700 hover:underline"
                    >
                      Abrir a esteira com a busca
                    </Link>
                  }
                />
                <ul className="divide-y divide-line">
                  {achados.map((cartao) => (
                    <ItemDaBusca
                      key={cartao.id}
                      cartao={cartao}
                      caminho={caminhoDoCartao(esteira.id, busca, cartao.id)}
                    />
                  ))}
                </ul>
              </Card>
            );
          })}
        </section>
      ) : null}
    </div>
  );
}

function ItemDaBusca({ cartao, caminho }: { cartao: PipeCard; caminho: string }) {
  const resumo = resumoDoCartao(cartao);
  return (
    <li>
      <Link
        to={caminho}
        className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 px-5 py-3 transition-colors hover:bg-brand-50/40 focus-visible:outline-2 focus-visible:outline-brand-500"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink-900">{resumo.titulo}</p>
          {resumo.linhas.map((linha) => (
            <p key={linha} className="truncate text-2xs text-ink-500">
              {linha}
            </p>
          ))}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <Badge tone="neutral">{cartao.step.name}</Badge>
          {cartao.is_closed ? <Badge tone="neutral">Encerrado</Badge> : null}
          {cartao.is_overdue ? <Badge tone="warning">Prazo vencido</Badge> : null}
          {cartao.operation ? (
            <span className="text-2xs tabular-nums text-ink-500">
              {formatMoney(cartao.operation.insured_amount)}
            </span>
          ) : null}
        </div>
      </Link>
    </li>
  );
}
